/**
 * Pure helpers for the Suppliers panel on the admin order page.
 * No Angular / no runtime imports so they stay trivially testable.
 */

export type SupplierStage =
  | "awaiting"
  | "received"
  | "approved"
  | "paid"
  | "rejected";

const STAGE_LABEL: Record<SupplierStage, string> = {
  awaiting: "Awaiting quote",
  received: "Quote received",
  approved: "Approved · to pay",
  paid: "Paid · in production",
  rejected: "Quote rejected",
};

/** Raw quote status → card stage. Missing/unknown → awaiting. */
export function supplierStage(status?: string): SupplierStage {
  switch (status) {
    case "submitted":
      return "received";
    case "approved":
      return "approved";
    case "paid":
      return "paid";
    case "rejected":
      return "rejected";
    default:
      return "awaiting";
  }
}

export function stageLabel(stage: SupplierStage): string {
  return STAGE_LABEL[stage];
}

/**
 * Ordered supplier tasks for a DIY order: Requested, Received, Approved,
 * Alibaba link, Paid, Tracking number.
 *
 * The Alibaba link is what the shop pays through, so it precedes "Paid"; the
 * tracking number is the only task left after payment. Step completion comes
 * from the quote status plus whether the Alibaba link / tracking exist, so a
 * step can complete out of order (e.g. tracking added before marked paid).
 */
export interface SupplierStep {
  label: string;
  state: "done" | "current" | "todo" | "rejected";
}

const STEP_LABELS = [
  "Requested",
  "Received",
  "Approved",
  "Alibaba link",
  "Paid",
  "Tracking number",
];

export function supplierSteps(
  status: string | undefined,
  hasAlibaba: boolean,
  hasTracking: boolean,
): SupplierStep[] {
  const stage = supplierStage(status);
  if (stage === "rejected") {
    return [
      { label: "Requested", state: "done" },
      { label: "Received", state: "done" },
      { label: "Rejected", state: "rejected" },
    ];
  }
  const approved = stage === "approved" || stage === "paid";
  const received = stage === "received" || approved;
  const done = [
    true,
    received,
    approved,
    hasAlibaba,
    stage === "paid",
    hasTracking,
  ];
  const firstOpen = done.findIndex((d) => !d);
  return STEP_LABELS.map((label, i) => ({
    label,
    state: done[i] ? "done" : i === firstOpen ? "current" : "todo",
  }));
}

export interface ScopeItem {
  supplierId?: string;
  roomName?: string;
  isRemote?: boolean;
}

export interface SupplierScope {
  rooms: string[];
  windowCount: number;
  remoteCount: number;
}

/** Which rooms/windows/remotes one supplier is building, from the order items. */
export function supplierScope(
  items: ScopeItem[],
  supplierId: string,
): SupplierScope {
  const mine = items.filter((it) => it.supplierId === supplierId);
  const windows = mine.filter((it) => !it.isRemote);
  const rooms = [
    ...new Set(
      windows
        .map((it) => (it.roomName ?? "").trim())
        .filter((name) => name !== ""),
    ),
  ];
  return {
    rooms,
    windowCount: windows.length,
    remoteCount: mine.filter((it) => it.isRemote).length,
  };
}

/** Action-needed first: review, pay, awaiting, rejected, done. */
const RANK: Record<SupplierStage, number> = {
  received: 0,
  approved: 1,
  awaiting: 2,
  rejected: 3,
  paid: 4,
};

export function sortSupplierIds(
  ids: string[],
  statuses: Record<string, string> = {},
): string[] {
  // Array.prototype.sort is stable, so equal-rank ids keep their input order.
  return [...ids].sort(
    (a, b) =>
      RANK[supplierStage(statuses[a])] - RANK[supplierStage(statuses[b])],
  );
}
